import OwnerAnalysisEntry, {isAnalysisOwner} from "./OwnerAnalysisEntry";
import AutomationStatusCard from "./AutomationStatusCard";
import { FEATURES, hasFeatureAccess, getUserAccessLevel } from "../helpers/featureAccess";
import { ThemeProvider } from "@mui/material/styles";
import { workareaTheme } from "../helpers/workareaTheme";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  CasinoOutlined,
  CalculateOutlined,
  FavoriteBorderOutlined,
  HelpOutline,
  InsightsOutlined,
  SettingsOutlined,
  SportsSoccerOutlined,
  StorageOutlined,
  GroupsOutlined,
  GridOnOutlined,
  TimelineOutlined,
} from "@mui/icons-material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const SYSTEM_BET_CALCULATOR_URL =
  "https://paulsen.blog/apps/systemwettenrechner/index.html";
export const CONTROL_CENTER_NAV_HEIGHT = 56;

export function ControlCenterNavigation({
  activeArea,
  user,
  onOpenPage,
  onStart,
  disabled = false,
  onControlCenter,
  onGames,
  onTeams,
  onStatistics,
  onSimulation,
  onOracle,
  onBetting,
  onDataMaintenance,
  onSettings,
  onHelp,
}) {
  const { t } = useTranslation();
  const [more, setMore] = useState(false);
  const items = [
    { key: "start", label: t("help.start.home"), onClick: onStart },
    { key: "oracle", label: t("help.start.oracle"), onClick: onOracle },
    { key: "games", label: t("help.start.games"), onClick: onGames },
    {
      key: "models",
      label: t("help.start.models"),
      onClick: onControlCenter,
    },
    { key: "settings", label: t("help.start.settings"), onClick: onSettings },
    { key: "help", label: t("help.start.help"), onClick: onHelp },
    ...(more
      ? [
          ...["profile", "tips", "history"].map(key=>({key,label:t(`help.start.${key}`),onClick:onOpenPage ? ()=>onOpenPage(key) : undefined})),
          ...(onDataMaintenance
            ? [
                {
                  key: "dataMaintenance",
                  label: t("help.start.update"),
                  onClick: onDataMaintenance,
                },
              ]
            : []),
        ]
      : []),
  ].filter((item) => item.onClick);

  return (
    <Paper
      component="nav"
      square
      elevation={2}
      aria-label={t("help.controlCenter.navigation.label")}
      sx={{
        position: "fixed",
        inset: "0 0 auto 0",
        zIndex: (theme) => theme.zIndex.modal + 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "#101f2b",
        color: "#eaf2f6",
        "& .MuiButton-root": { color: "inherit" },
      }}
    >
      <Box sx={{display:"flex",alignItems:"center"}}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.25}
        sx={{
          minHeight: CONTROL_CENTER_NAV_HEIGHT,
          minWidth:0, flex:1,
          px: { xs: 1, md: 2 },
          overflowX: "auto",
        }}
      >
        {items.map((item) => (
          <Button
            key={item.key}
            size="small"
            aria-current={activeArea === item.key ? "page" : undefined}
            variant={activeArea === item.key ? "contained" : "text"}
            disabled={disabled}
            onClick={item.onClick}
            href={item.href}
            target={item.href ? "_blank" : undefined}
            rel={item.href ? "noreferrer" : undefined}
            sx={{ whiteSpace: "nowrap", textTransform: "none", flexShrink: 0 }}
          >
            {item.label}
          </Button>
        ))}
        <Button
          size="small"
          onClick={() => setMore(!more)}
          aria-expanded={more}
          aria-current={["profile","tips","history","dataMaintenance"].includes(activeArea) && !more ? "page" : undefined}
          sx={{ color: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {t(more ? "help.start.less" : "help.start.more")}
        </Button>
      </Stack>
      {user && <Button data-testid="active-profile" onClick={onSettings} disabled={disabled}
        title={t("help.navigation.profileAction")}
        sx={{flexShrink:0,maxWidth:{xs:135,sm:260},textTransform:"none",textAlign:"right",display:"block"}}>
        <Typography noWrap variant="body2">{user.name || user.email || user.user_id || user.id}</Typography>
        <Typography variant="caption">{getUserAccessLevel(user).toUpperCase()} · {t("help.navigation.profile")}</Typography>
      </Button>}
      </Box>
    </Paper>
  );
}

function ControlCenterTile({
  icon,
  title,
  description,
  onClick,
  href,
  status,
  actions = [],
  featured = false,
}) {
  const content = (
    <CardContent sx={{ p: { xs: 2.25, md: 2.75 }, height: "100%" }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
      >
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 46,
            height: 46,
            borderRadius: 2.5,
            bgcolor: "rgba(123,60,89,0.1)",
            color: "primary.main",
          }}
        >
          {icon}
        </Box>
        {status}
      </Stack>
      <Typography
        variant="h6"
        sx={{ mt: 2, fontWeight: 800, letterSpacing: "0.025em" }}
      >
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
        {description}
      </Typography>
    </CardContent>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        minHeight: featured ? 230 : 190,
        gridColumn: featured ? {xs:"auto",lg:"span 2"} : "auto",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        borderColor: "rgba(123,60,89,0.2)",
        bgcolor: featured ? "rgba(38,66,81,.95)" : "rgba(25,44,58,.96)",
        boxShadow: "0 8px 24px rgba(74,35,54,0.07)",
        transition:
          "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 16px 34px rgba(74,35,54,0.16)",
          borderColor: "primary.light",
        },
      }}
    >
      {onClick || href ? (
        <CardActionArea
          onClick={onClick}
          href={href}
          target={href ? "_blank" : undefined}
          rel={href ? "noreferrer" : undefined}
          sx={{ flex: 1, alignItems: "stretch" }}
        >
          {content}
        </CardActionArea>
      ) : (
        content
      )}
      {actions.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ px: 2.25, pb: 2 }}>
          {actions.map((action) => (
            <Button
              key={action.label}
              size="small"
              onClick={action.onClick}
              href={action.href}
              target={action.href ? "_blank" : undefined}
              rel={action.href ? "noreferrer" : undefined}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      )}
    </Card>
  );
}

export function ControlCenter({
  user,
  onOpenPage,
  onAdminUsers,
  onAdminTools,
  onRealBetting,
  onGames,
  onTeams,
  onStandings,
  onStatistics,
  onSimulation,
  onOracle,
  onBetting,
  onSettings,
  onDataMaintenance,
  onWhatsNew,
  onChess,
  isAdmin,
  dataMaintenanceAvailable,
  hasNewWhatsNew,
  plansUrl,
  donateUrl,
}) {
  const { t } = useTranslation();
  const [ownerAnalysisOpen, setOwnerAnalysisOpen] = useState(false);
  const tiles = [
    ...(isAnalysisOwner(user) ? [{key:"owner-analysis", icon:<InsightsOutlined/>, title:"Mein Analyse-Arbeitsplatz", description:"Liga → Mannschaft → Änderung → Tipp · geführter Einstieg nur für dich", onClick:()=>setOwnerAnalysisOpen(true), featured:true}] : []),
    { key: "games", icon: <SportsSoccerOutlined />, onClick: onGames, featured:true },
    ...["profile","tips","history","models"].map(key=>({key,icon:key==="profile"?<SettingsOutlined/>:<InsightsOutlined/>,title:t(`help.start.${key}`),description:t(`help.start.${key}Text`),onClick:()=>onOpenPage?.(key)})),
    ...(hasFeatureAccess(FEATURES.STATISTICS_MODEL,user)?[{key:"statistics-model",icon:<CalculateOutlined/>,title:t("help.navigation.statisticsModel"),description:t("help.navigation.statisticsDescription"),onClick:()=>onOpenPage?.("statistics-model")}]:[]),
    ...(hasFeatureAccess(FEATURES.ALL_IN_ONE,user)?[{key:"all-in-one",icon:<GridOnOutlined/>,title:"All In One · Admin",description:t("help.navigation.allInOneDescription"),onClick:()=>onOpenPage?.("all-in-one")}]:[]),
    { key: "teams", icon: <GroupsOutlined />, onClick: onTeams },
    {
      key: "statistics",
      icon: <InsightsOutlined />,
      onClick: onStatistics,
      actions: [
        {
          label: t("help.controlCenter.actions.standings"),
          onClick: onStandings,
        },
      ],
    },
    { key: "simulation", icon: <TimelineOutlined />, onClick: onSimulation },
    { key: "betting", icon: <CasinoOutlined />, onClick: onBetting, actions:[{label:"Real Bets",onClick:onRealBetting}] },
    {
      key: "systemBetCalculator",
      icon: <CalculateOutlined />,
      href: SYSTEM_BET_CALCULATOR_URL,
    },
    {
      key: "dataMaintenance",
      icon: <StorageOutlined />,
      onClick: onDataMaintenance,
      status: (
        <Chip
          size="small"
          color={dataMaintenanceAvailable ? "warning" : "success"}
          label={t(
            dataMaintenanceAvailable
              ? "help.controlCenter.maintenanceAvailable"
              : "help.controlCenter.maintenanceClean"
          )}
        />
      ),
    },
    { key: "settings", icon: <SettingsOutlined />, onClick: onSettings },
    {
      key: "helpNews",
      icon: <HelpOutline />,
      onClick: onWhatsNew,
      status: hasNewWhatsNew ? (
        <Chip size="small" color="secondary" label="NEW" />
      ) : null,
      actions: [{ label: t("help.controlCenter.actions.help"), href: "/help" }, {label:"Docs",href:"https://docs.betty.works"}],
    },
    {
      key: "planSupport",
      icon: <FavoriteBorderOutlined />,
      href: plansUrl,
      actions: [
        { label: t("help.controlCenter.actions.support"), href: donateUrl },
      ],
    },
  ];

  if (isAdmin) {
    tiles.push({key:"adminUsers",title:t("help.navigation.adminUsers"),description:t("help.navigation.adminUsersDescription"),icon:<GroupsOutlined/>,onClick:onAdminUsers},
      {key:"adminTools",title:t("help.navigation.adminTools"),description:t("help.navigation.adminToolsDescription"),icon:<StorageOutlined/>,onClick:onAdminTools});
    tiles.push({
      key: "chess",
      icon: <GridOnOutlined />,
      onClick: onChess,
    });
  }

  return (
    <ThemeProvider theme={workareaTheme}>
      {ownerAnalysisOpen && <OwnerAnalysisEntry user={user} onClose={()=>setOwnerAnalysisOpen(false)}/>}
      <Box sx={{ maxWidth: 1440, mx: "auto", p:{xs:1,md:3}, pb:5, color:"text.primary", borderRadius:3, background:"linear-gradient(rgba(10,24,34,.84),rgba(10,24,34,.96)), url(/betty-start-bg.png) center/cover" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3.5 }}
      >
        <Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 800, color: "primary.main" }}
          >
            {t("help.controlCenter.title")}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
            {t("help.controlCenter.subtitle")}
          </Typography>
        </Box>
      </Stack>

      {onDataMaintenance && <AutomationStatusCard onDetails={onDataMaintenance} />}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
          gap: { xs: 2, md: 2.5 },
        }}
      >
        {tiles.filter(tile=>tile.key!=="dataMaintenance" || onDataMaintenance).map((tile) => (
          <ControlCenterTile
            key={tile.key}
            icon={tile.icon}
            title={tile.title || t(`help.controlCenter.tiles.${tile.key}.title`)}
            featured={tile.featured}
            description={tile.description || t(`help.controlCenter.tiles.${tile.key}.description`)}
            onClick={tile.onClick}
            href={tile.href}
            status={tile.status}
            actions={tile.actions}
          />
        ))}
      </Box>
    </Box></ThemeProvider>
  );
}
